import { SEO, BreadcrumbSchema, SpeakableSchema, FaqSchema } from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { CTABanner } from '@/components/CTABanner';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Clock, CheckCircle2, Tag, CreditCard, Shield } from 'lucide-react';

/* ─── Hero ─────────────────────────────────────────────── */
const PageHero = () => (
  <section className="relative pt-28 pb-14 bg-navy text-white overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center max-w-4xl mx-auto">
        <p className="section-eyebrow justify-center">Save on Your Deck Project</p>
        <h1
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase leading-[1.05]"
          data-speakable
        >
          Current <span className="text-lime">Deck Offers</span>
        </h1>
        <p
          className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed font-body"
          data-speakable
        >
          Limited-time savings on new deck builds, replacements, and repairs across New Jersey. Plus 0% APR financing for qualified homeowners.
        </p>
      </div>
    </div>
  </section>
);

/* ─── Offer cards data ─────────────────────────────── */
interface OfferData {
  slug: string;
  amount: string;
  title: string;
  tagline: string;
  description: string;
  minSpend: string;
  expires: string;
  featured?: boolean;
}

const offersData: OfferData[] = [
  {
    slug: 'deck-repairs',
    amount: '$399',
    title: 'Deck Repairs',
    tagline: 'Off Any Repair Project',
    description: 'Save $399 on structural repairs, board replacement, railing fixes, staining, or sealing.',
    minSpend: '$1,000+',
    expires: 'Dec 31, 2026',
  },
  {
    slug: 'new-deck-build',
    amount: '$1,800',
    title: 'New Deck Build',
    tagline: 'Off Custom Deck Construction',
    description: 'Save $1,800 on any custom deck design + build — wood, composite, or PVC decking.',
    minSpend: '$8,000+',
    expires: 'Dec 31, 2026',
    featured: true,
  },
  {
    slug: 'deck-replacement',
    amount: '$1,200',
    title: 'Deck Replacement',
    tagline: 'Off Full Replacement Projects',
    description: 'Save $1,200 on complete deck tear-out and rebuild with upgraded materials.',
    minSpend: '$10,000+',
    expires: 'Dec 31, 2026',
  },
];

/* ─── Offer card ─────────────────────────────── */
const OfferCard = ({ offer, index }: { offer: OfferData; index: number }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      itemScope
      itemType="https://schema.org/Offer"
      id={offer.slug}
      className="relative"
    >
      <meta itemProp="priceCurrency" content="USD" />
      <meta itemProp="validThrough" content="2026-12-31" />
      {offer.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-navy text-lime font-display font-bold text-[10px] uppercase tracking-[0.25em] px-4 py-1.5 border-2 border-lime z-10">
          ★ Best Value ★
        </div>
      )}

      <div className={`bg-white border-2 transition-all duration-200 hover:-translate-y-1 hover:shadow-hard h-full flex flex-col ${
        offer.featured ? 'border-lime shadow-hard' : 'border-navy/10 hover:border-lime'
      }`}>
        {/* Top accent */}
        <div className="h-2 bg-lime" />

        <div className="p-7 md:p-8 flex flex-col flex-1 text-center">
          {/* Limited-time pill */}
          <div className="mb-5 flex items-center justify-center gap-2">
            <Clock className="w-3.5 h-3.5 text-patriot" />
            <span className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-stone">
              Limited Time · Exp {offer.expires}
            </span>
          </div>

          {/* Save amount */}
          <div className="mb-2">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.3em] text-stone mb-1">
              Save
            </div>
            <div
              itemProp="price"
              data-speakable
              className="font-display text-6xl md:text-7xl font-bold text-lime leading-none"
              style={{ textShadow: '3px 3px 0 hsl(var(--navy))' }}
            >
              {offer.amount}
            </div>
          </div>

          <h3
            itemProp="name"
            className="font-display text-xl font-bold text-navy uppercase tracking-wide mt-4 mb-1"
          >
            {offer.title}
          </h3>
          <p className="text-xs font-display font-semibold text-lime uppercase tracking-[0.15em] mb-4">
            {offer.tagline}
          </p>
          <p itemProp="description" className="text-stone text-sm leading-relaxed mb-5 font-body">
            {offer.description}
          </p>

          {/* Min spend + expires */}
          <div className="flex flex-col gap-2 mb-6 text-xs">
            <div className="flex items-center justify-center gap-2 bg-cream border border-navy/15 px-3 py-1.5 font-display font-bold text-navy uppercase tracking-wider">
              <Tag className="w-3 h-3 text-lime" />
              Min project: {offer.minSpend}
            </div>
          </div>

          <Link to="/contact" className="mt-auto">
            <Button variant="cta" size="lg" className="w-full">
              Claim This Offer
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

/* ─── Offers grid ─────────────────────────────── */
const OffersGrid = () => (
  <section className="section-compact bg-cream">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Active Savings</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Save Up To <span className="text-lime">$1,800</span>
        </h2>
        <p className="text-stone font-body">
          Three active promotions on deck work in New Jersey. Mention the offer during your free consultation.
        </p>
      </div>

      <div
        itemScope
        itemType="https://schema.org/ItemList"
        className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto"
      >
        {offersData.map((offer, i) => (
          <OfferCard key={offer.slug} offer={offer} index={i} />
        ))}
      </div>

      <p className="text-center text-xs text-stone/70 mt-6 font-body italic">
        All offers valid on new contracts signed in 2026. Cannot be combined.
      </p>
    </div>
  </section>
);

/* ─── Financing strip ─────────────────────────────── */
const FinancingStrip = () => (
  <section className="bg-lime border-y-[6px] border-navy py-8 md:py-10">
    <div className="container mx-auto px-4">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-6xl mx-auto text-navy">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-navy flex items-center justify-center border-2 border-navy flex-shrink-0">
            <CreditCard className="w-7 h-7 text-lime" />
          </div>
          <div>
            <p className="font-display text-[11px] uppercase tracking-[0.24em] mb-1 font-bold opacity-80">
              Financing Available
            </p>
            <h3 className="font-display text-2xl md:text-3xl font-bold uppercase leading-tight" data-speakable>
              0% APR · <span className="text-white" style={{ textShadow: '2px 2px 0 hsl(var(--navy))' }}>12 Months</span>
            </h3>
          </div>
        </div>
        <p className="text-navy/90 text-sm max-w-md font-body md:text-right">
          Extended plans up to <strong>84 months</strong> for qualified homeowners. Quick approval. No prepayment penalties.
        </p>
        <Link to="/contact">
          <Button variant="default" size="lg">
            Apply Today
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

/* ─── How to Claim (HowTo schema) ─────────────────── */
const HowToClaim = () => {
  const steps = [
    { n: '01', title: 'Request Free Quote', desc: 'Call (201) 657-0855 or fill out the form for a free on-site estimate.' },
    { n: '02', title: 'Mention Your Offer', desc: 'Tell us which promotion you want applied during your consultation.' },
    { n: '03', title: 'Lock in Savings', desc: 'Sign your contract within 30 days to lock your discounted price.' },
    { n: '04', title: 'Enjoy Your New Deck', desc: 'Our in-house crew builds and finishes the job within 6 months.' },
  ];

  return (
    <section className="section-compact bg-navy text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="section-eyebrow justify-center">Redemption</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold">
            How to Claim Your <span className="text-lime">Discount</span>
          </h2>
        </div>

        <ol
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto"
          itemScope
          itemType="https://schema.org/HowTo"
        >
          {steps.map((s) => (
            <li
              key={s.n}
              itemScope
              itemType="https://schema.org/HowToStep"
              className="relative bg-navy-deep border-2 border-white/10 hover:border-lime transition-colors p-6"
            >
              <div className="absolute -top-3 -left-3 bg-lime text-navy font-display font-bold text-lg w-10 h-10 flex items-center justify-center border-2 border-navy">
                {s.n}
              </div>
              <h3 itemProp="name" className="font-display text-base font-bold text-lime mb-2 uppercase tracking-wide mt-2">
                {s.title}
              </h3>
              <p itemProp="text" className="text-white/70 text-sm leading-relaxed font-body">
                {s.desc}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ─── Terms ─────────────────────────────── */
const Terms = () => (
  <section className="section-compact bg-cream-dark">
    <div className="container mx-auto px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <p className="section-eyebrow justify-center">The Fine Print</p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-navy">
            Offer <span className="text-lime">Terms</span>
          </h2>
        </div>

        <div className="bg-white border-2 border-navy/15 p-6 md:p-8">
          <ul className="space-y-2.5">
            {[
              'Offers valid on new contracts signed between Jan 1 – Dec 31, 2026',
              'Cannot be combined with other offers or promotions',
              'Minimum project values apply (as listed on each offer card)',
              'Not valid on previous estimates, contracts, or completed work',
              'Work must be completed within 6 months of contract signing',
              'Offers subject to change or withdrawal without notice',
              'All standard DeckPros NJ terms and conditions apply',
              'Financing subject to credit approval — ask for details',
            ].map((term) => (
              <li key={term} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-lime flex-shrink-0 mt-1" />
                <span className="text-stone text-sm font-body leading-relaxed">{term}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ─── FAQ (AEO / GEO) ───────────────────────────── */
const offersFaqs = [
  {
    question: 'How much can I save on a new deck with DeckPros NJ?',
    answer: 'DeckPros NJ offers up to $1,800 off on new custom deck builds with a minimum project of $8,000+, $1,200 off on complete deck replacements with a minimum $10,000+ project, and $399 off on any deck repair over $1,000. Savings are valid on new contracts signed in 2026.',
  },
  {
    question: 'Can I combine multiple offers?',
    answer: 'No — each DeckPros NJ promotion must be used individually. Our best-value offer is $1,800 off on new deck builds. Financing (0% APR for 12 months) can be combined with any single promotion.',
  },
  {
    question: 'How do I claim a deck offer?',
    answer: 'To claim a DeckPros NJ offer, call (201) 657-0855 or request a free estimate online. Mention the specific promotion during your consultation, and the discount will be applied to your written quote. Sign the contract within 30 days to lock in savings.',
  },
  {
    question: 'Do you offer financing for deck projects in NJ?',
    answer: 'Yes. DeckPros NJ offers 0% APR financing for 12 months on approved credit, plus extended payment plans up to 84 months. Financing is available on projects starting at $2,500 and requires a quick credit check.',
  },
  {
    question: 'When do these deck offers expire?',
    answer: 'All current DeckPros NJ promotions are valid on new contracts signed between January 1 and December 31, 2026. Work must be completed within 6 months of contract signing.',
  },
  {
    question: 'Are these offers available in all NJ counties?',
    answer: 'Yes. All DeckPros NJ offers are available throughout our service area — Morris, Sussex, Warren, Hunterdon, and Somerset Counties, plus surrounding northern and central NJ towns.',
  },
];

const FAQSection = () => (
  <section className="section-compact bg-cream">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10 max-w-2xl mx-auto">
        <p className="section-eyebrow justify-center">Offer Questions</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-3">
          Promotion <span className="text-lime">FAQs</span>
        </h2>
      </div>

      <div
        className="max-w-4xl mx-auto space-y-3"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        {offersFaqs.map((faq, i) => (
          <details
            key={i}
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
            className="group bg-white border-2 border-navy/15 hover:border-lime transition-colors [&[open]]:border-lime [&[open]]:shadow-hard"
          >
            <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
              <h3
                itemProp="name"
                className="font-display text-sm md:text-base font-bold text-navy uppercase tracking-wide group-open:text-lime transition-colors"
              >
                {faq.question}
              </h3>
              <span className="font-display text-2xl text-lime leading-none transition-transform group-open:rotate-45 flex-shrink-0">+</span>
            </summary>
            <div
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
              className="px-5 pb-5 -mt-1"
            >
              <p itemProp="text" className="text-stone text-sm leading-relaxed font-body border-t border-navy/10 pt-4">
                {faq.answer}
              </p>
            </div>
          </details>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Talk-to-us banner ─────────────────────────── */
const QuestionsBar = () => (
  <section className="bg-navy-deep py-10">
    <div className="container mx-auto px-4 text-center">
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 max-w-3xl mx-auto">
        <Shield className="w-8 h-8 text-lime flex-shrink-0" />
        <div className="text-white text-center md:text-left">
          <p className="font-display text-[11px] uppercase tracking-[0.24em] text-lime mb-1 font-bold">
            Questions About Offers?
          </p>
          <p className="font-body text-white/75 text-sm">
            Our team can explain any promotion, confirm eligibility, and answer financing questions.
          </p>
        </div>
        <a href="tel:2016570855">
          <Button variant="cta" size="lg">
            <Phone className="w-4 h-4" />
            (201) 657-0855
          </Button>
        </a>
      </div>
    </div>
  </section>
);

/* ─── Offer ItemList Schema ───────────────────── */
const OffersItemListSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'DeckPros NJ Current Deck Offers',
    numberOfItems: offersData.length,
    itemListElement: offersData.map((o, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Offer',
        name: `${o.amount} Off ${o.title}`,
        description: o.description,
        price: o.amount.replace(/[$,]/g, ''),
        priceCurrency: 'USD',
        validThrough: '2026-12-31',
        url: `https://deckprosnj.com/offers#${o.slug}`,
        eligibleRegion: { '@type': 'State', name: 'New Jersey' },
        seller: {
          '@type': 'LocalBusiness',
          name: 'DeckPros NJ',
          telephone: '+1-201-657-0855',
        },
      },
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

/* ─── Page ─────────────────────────────────────── */
const Offers = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Current Deck Offers & Financing"
        description="Save up to $1,800 on deck projects with DeckPros NJ. Active offers: $399 off repairs, $1,800 off new builds, $1,200 off replacements. 0% APR financing available. Serving all of New Jersey."
        canonical="/offers"
        keywords="deck offers NJ, deck promotions New Jersey, new deck discount, deck financing NJ, 0% APR deck, deck repair discount, deck coupon, deck builder special NJ"
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: '/' },
        { name: 'Offers', url: '/offers' },
      ]} />
      <SpeakableSchema />
      <FaqSchema faqs={offersFaqs} />
      <OffersItemListSchema />

      <Header />
      <main>
        <PageHero />
        <OffersGrid />
        <FinancingStrip />
        <HowToClaim />
        <Terms />
        <FAQSection />
        <QuestionsBar />
        <CTABanner />
      </main>
      <Footer />
    </div>
  );
};

export default Offers;
